<template>
    <v-container class="mt-10 pb-12"
        style="min-width: 1200px; max-width: 1600px; padding-left: 24px; padding-right: 24px;">
        <!-- Header Section -->
        <div class="mb-6">
            <div class="d-flex align-center justify-space-between mb-4">
                <div>
                    <div class="d-flex align-center" style="gap:12px;">
                        <h1 class="text-h4 font-weight-bold mb-0">Employee Directory</h1>
                        <v-chip color="primary" small class="font-weight-bold">
                            <v-icon left x-small>mdi-account-group</v-icon>
                            {{ totalEmployeeCount }} {{ totalEmployeeCount === 1 ? 'Employee' : 'Employees' }}
                        </v-chip>
                    </div>
                    <p class="text-subtitle-1 grey--text mb-0 mt-2">
                        Find the right team member for your project
                        <span v-if="hasActiveFilters || searchQuery" class="font-weight-medium">
                            ({{ totalFilteredUsers }} {{ totalFilteredUsers === 1 ? 'user' : 'users' }} found)
                        </span>
                    </p>
                </div>
                <div class="d-flex" style="gap: 8px;">
                    <v-btn color="primary" outlined :loading="refreshing" @click="refreshData" class="text-none">
                        <v-icon left>mdi-refresh</v-icon>
                        Refresh
                    </v-btn>
                    <v-btn v-if="canAddEmployee" color="primary" @click="openCreateUserDialog" class="text-none">
                        <v-icon left>mdi-account-plus</v-icon>
                        Add Employee
                    </v-btn>
                </div>
            </div>
        </div>

        <!-- Company and Role Filter Tabs -->
        <v-row class="mb-4">
            <!-- Company Filter -->
            <v-col cols="12" md="6">
                <v-card elevation="0" outlined class="filter-card">
                    <v-card-title class="py-3">
                        <v-icon small class="mr-2">mdi-domain</v-icon>
                        <span class="text-subtitle-2 font-weight-bold">Companies</span>
                    </v-card-title>
                    <v-card-text class="pt-0">
                        <v-chip-group v-model="selectedCompany" class="filter-chips">
                            <v-chip filter :value="null" :color="selectedCompany === null ? 'primary' : 'default'"
                                :text-color="selectedCompany === null ? 'white' : 'default'" small>
                                All
                            </v-chip>
                            <v-chip v-for="company in companies()" :key="company.id" filter :value="company.id"
                                :color="selectedCompany === company.id ? 'primary' : 'default'"
                                :text-color="selectedCompany === company.id ? 'white' : 'default'" small>
                                {{ company.name }}
                            </v-chip>
                        </v-chip-group>
                    </v-card-text>
                </v-card>
            </v-col>

            <!-- Role Filter -->
            <v-col cols="12" md="6">
                <v-card elevation="0" outlined class="filter-card">
                    <v-card-title class="py-3">
                        <v-icon small class="mr-2">mdi-account-group</v-icon>
                        <span class="text-subtitle-2 font-weight-bold">Teams</span>
                    </v-card-title>
                    <v-card-text class="pt-0">
                        <v-chip-group v-model="selectedRole" class="filter-chips">
                            <v-chip filter :value="null" :color="selectedRole === null ? 'primary' : 'default'"
                                :text-color="selectedRole === null ? 'white' : 'default'" small>
                                All
                            </v-chip>
                            <v-chip v-for="role in sortedRoles" :key="role.id" filter :value="role.id"
                                :color="selectedRole === role.id ? 'primary' : 'default'"
                                :text-color="selectedRole === role.id ? 'white' : 'default'" small>
                                {{ role.name }}
                            </v-chip>
                        </v-chip-group>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <!-- Search Bar -->
        <v-card elevation="0" outlined class="mb-6 search-card">
            <v-card-text>
                <v-text-field v-model="searchQuery"
                    placeholder="Search by name, email, position, role, skills, or location..." outlined dense
                    hide-details prepend-inner-icon="mdi-magnify" clearable></v-text-field>
            </v-card-text>
        </v-card>

        <!-- Loading State -->
        <v-skeleton-loader v-if="loading" type="card, card, card"></v-skeleton-loader>

        <!-- User Directory Content -->
        <div v-else>
            <!-- Grouped by Role/Department -->
            <div v-for="(group, groupName) in groupedUsers" :key="groupName" class="mb-8">
                <div class="d-flex align-center mb-4">
                    <div class="group-indicator"></div>
                    <h2 class="text-h5 font-weight-bold ml-3">{{ groupName }}</h2>
                    <v-chip class="ml-3" small>{{ group.length }} member{{ group.length !== 1 ? 's' : '' }}</v-chip>
                </div>

                <!-- User Cards Grid -->
                <v-row>
                    <v-col v-for="user in group" :key="user.id" cols="12" sm="6" md="4" lg="4">
                        <v-card class="user-card" elevation="0" outlined hover>
                            <v-card-text class="pa-6" :class="{ 'directory-card-limited': !isSuperAdmin }"
                                @click="isSuperAdmin && viewUserDetails(user)">
                                <!-- Avatar and Basic Info -->
                                <div class="d-flex align-start mb-4">
                                    <v-avatar color="primary" size="64" class="white--text text-h5 font-weight-medium">
                                        <v-img v-if="user.profile_picture"
                                            :src="getProfilePictureUrl(user.profile_picture)"></v-img>
                                        <span v-else>{{ getUserInitials(user) }}</span>
                                    </v-avatar>
                                    <div class="ml-4 flex-grow-1">
                                        <h3 class="text-h6 font-weight-bold mb-1">
                                            {{ getUserFullName(user) }}
                                        </h3>
                                        <p class="text-subtitle-2 text--secondary mb-0">
                                            {{ user.position_name || user.role?.name || 'No Position' }}
                                        </p>
                                    </div>
                                    <v-btn v-if="canViewUserTasks(user.id)" icon small
                                        @click.stop="viewUserKpis(user)" class="edit-btn" title="View KPIs">
                                        <v-icon small color="primary">mdi-chart-line</v-icon>
                                    </v-btn>
                                    <v-btn v-if="canImpersonate(user)" icon small @click.stop="impersonateUser(user)"
                                        :loading="impersonatingUserId === user.id" class="edit-btn" title="Impersonate user">
                                        <v-icon small color="primary">mdi-account-switch</v-icon>
                                    </v-btn>
                                    <v-btn v-if="isAdmin && !isCurrentUser(user)" icon small @click.stop="openChangePasswordDialog(user)"
                                        class="edit-btn" title="Change Password">
                                        <v-icon small>mdi-lock-reset</v-icon>
                                    </v-btn>
                                    <v-btn v-if="isAdmin && !isCurrentUser(user)" icon small @click.stop="logoutUserAllSessions(user)"
                                        :loading="loggingOutUserId === user.id" class="edit-btn" title="Log out from all sessions">
                                        <v-icon small color="orange darken-2">mdi-logout-variant</v-icon>
                                    </v-btn>
                                    <v-btn v-if="isAdmin || isCurrentUser(user)" icon small @click.stop="openEditDialog(user)"
                                        class="edit-btn">
                                        <v-icon small>mdi-pencil</v-icon>
                                    </v-btn>
                                    <v-btn v-if="isSuperAdmin" icon small @click.stop="confirmDeleteUser(user)" class="delete-btn">
                                        <v-icon small color="#e53935">mdi-delete</v-icon>
                                    </v-btn>
                                </div>

                                <!-- Skills (full data view only) -->
                                <div v-if="isSuperAdmin && user.skills && user.skills.length" class="mb-3">
                                    <div class="d-flex flex-wrap">
                                        <v-chip v-for="(skill, index) in getDisplaySkills(user.skills)" :key="index"
                                            x-small label class="mr-2 mb-2 skill-chip">
                                            {{ skill }}
                                        </v-chip>
                                        <v-chip v-if="user.skills.length > 3" x-small label class="mb-2 more-chip">
                                            +{{ user.skills.length - 3 }}
                                        </v-chip>   
                                    </div>
                                </div>

                                <!-- Team Info -->
                                <div class="team-info">
                                    <div class="d-flex align-center text-caption text--secondary">
                                        <v-icon x-small class="mr-1">mdi-account-group</v-icon>
                                        <span v-if="user.role_members && user.role_members.length > 0">
                                            Team: <strong>{{ user.role_members.map(m => m.role?.name).filter(Boolean).join(', ') || groupName }}</strong>
                                        </span>
                                        <span v-else>Team: <strong>{{ groupName }}</strong></span>
                                    </div>
                                </div>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>
            </div>

            <!-- No Results -->
            <v-card v-if="Object.keys(groupedUsers).length === 0" elevation="0" outlined class="text-center pa-8">
                <v-icon size="64" color="grey lighten-1">mdi-account-search</v-icon>
                <p class="text-h6 mt-4 mb-2">No users found</p>
                <p class="text-body-2 text--secondary mb-4">
                    {{ getNoResultsMessage() }}
                </p>
                <v-btn v-if="hasActiveFilters || searchQuery" color="primary" outlined @click="clearAllFilters">
                    <v-icon small class="mr-2">mdi-filter-remove</v-icon>
                    Clear All Filters
                </v-btn>
            </v-card>
        </div>

        <!-- User Details Dialog -->
        <UserDetailsDialog 
            v-model="userDetailsDialog" 
            :user="selectedUser"
            :show-tasks="true"
            :tasks="userTasks"
            :user-stats="selectedUserStats"
            :loading-tasks="loadingTasks"
            :task-error="taskError"
            @close="closeUserDetailsDialog"
        >
            <template v-slot:actions="{ close }">
                <v-btn color="success" @click="openTaskCreationDialog">
                    <v-icon small class="mr-2">mdi-plus-circle</v-icon>
                    Assign Task
                </v-btn>
                <v-btn v-if="selectedUser && canViewUserTasks(selectedUser.id)" color="primary" outlined
                    @click="viewUserKpis(selectedUser)">
                    <v-icon small class="mr-2">mdi-chart-line</v-icon>
                    View KPIs
                </v-btn>
                <v-spacer></v-spacer>
                <v-btn v-if="isAdmin && !isCurrentUser(selectedUser)" color="warning" @click="openChangePasswordFromDetails()">
                    <v-icon small class="mr-2">mdi-lock-reset</v-icon>
                    Change Password
                </v-btn>
                <v-btn v-if="isAdmin || isCurrentUser(selectedUser)" color="primary" @click="openEditDialogFromDetails()">
                    <v-icon small class="mr-2">mdi-pencil</v-icon>
                    {{ isAdmin ? 'Edit User' : 'Edit Profile' }}
                </v-btn>
                <v-btn text @click="close">Close</v-btn>
            </template>
        </UserDetailsDialog>

        <!-- Edit User Dialog -->
        <v-dialog v-model="editUserDialog" max-width="800" persistent scrollable>
            <v-card>
                <v-card-title class="primary white--text">
                    <v-icon dark class="mr-2">mdi-account-edit</v-icon>
                    <span class="text-h5">{{ isEditingSelf ? 'Edit Profile' : 'Edit User Information' }}</span>
                    <v-spacer></v-spacer>
                    <v-btn icon dark @click="closeEditDialog">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>

                <v-card-text class="pa-6">
                    <v-form ref="editForm">
                        <v-row>
                            <!-- Basic Information -->
                            <v-col cols="12">
                                <h3 class="text-subtitle-1 font-weight-bold mb-3">Basic Information</h3>
                            </v-col>

                            <!-- Name (editable by all) -->
                            <v-col cols="12" md="6">
                                <v-text-field v-model="userToEdit.first_name" label="First Name" outlined dense
                                    prepend-inner-icon="mdi-account"></v-text-field>
                            </v-col>

                            <v-col cols="12" md="6">
                                <v-text-field v-model="userToEdit.last_name" label="Last Name" outlined
                                    dense></v-text-field>
                            </v-col>

                            <v-col cols="12" md="6">
                                <v-text-field v-model="userToEdit.name" label="Display Name *" outlined
                                    dense :rules="nameRules"></v-text-field>
                            </v-col>

                            <!-- Admin-only fields -->
                            <v-col v-if="!isEditingSelf" cols="12" md="6">
                                <v-text-field v-model="userToEdit.email" label="Email" outlined dense type="email"
                                    prepend-inner-icon="mdi-email"></v-text-field>
                            </v-col>

                            <v-col cols="12" md="6">
                                <v-text-field v-model="userToEdit.secondary_email" label="Secondary Email" outlined dense type="email"
                                    prepend-inner-icon="mdi-email-outline"></v-text-field>
                            </v-col>

                            <v-col v-if="!isEditingSelf" cols="12" md="6">
                                <v-text-field v-model="userToEdit.phone" label="Phone" outlined dense
                                    prepend-inner-icon="mdi-phone"></v-text-field>
                            </v-col>

                            <v-col v-if="!isEditingSelf" cols="12" md="6">
                                <v-select v-model="userToEdit.role_ids" label="Roles / Teams *" :items="get_roles"
                                    item-text="name" item-value="id" outlined dense multiple chips small-chips
                                    deletable-chips prepend-inner-icon="mdi-shield-account"></v-select>
                            </v-col>

                            <v-col v-if="!isEditingSelf" cols="12" md="6">
                                <v-text-field v-model="userToEdit.position_name" label="Position" outlined dense
                                    prepend-inner-icon="mdi-briefcase"></v-text-field>
                            </v-col>

                            <!-- Location (editable by all) -->
                            <v-col cols="12" md="6">
                                <v-text-field v-model="userToEdit.location" label="Location" outlined dense
                                    prepend-inner-icon="mdi-map-marker"></v-text-field>
                            </v-col>

                            <!-- Joined Date (editable by all) -->
                            <v-col cols="12" md="6">
                                <v-text-field v-model="userToEdit.joined_date" label="Date Started" outlined dense
                                    type="date" prepend-inner-icon="mdi-calendar"></v-text-field>
                            </v-col>

                            <!-- Profile picture upload (admin only for now) -->
                            <v-col v-if="!isEditingSelf" cols="12" md="6">
                                <v-file-input v-model="profilePictureFile" label="Profile Picture" outlined dense
                                    prepend-icon="" prepend-inner-icon="mdi-camera" @change="uploadProfilePicture"
                                    :loading="uploading" hint="Upload an image file (JPEG, PNG, GIF - Max 5MB)"
                                    persistent-hint show-size accept="image/*" clearable
                                    :disabled="uploading"></v-file-input>
                                <v-progress-linear v-if="uploading" indeterminate color="primary"
                                    class="mt-2"></v-progress-linear>
                            </v-col>

                            <v-col cols="12" v-if="!isEditingSelf && userToEdit.profile_picture">
                                <v-alert dense outlined type="success" class="mb-0">
                                    <div class="d-flex align-center">
                                        <v-avatar size="40" class="mr-3">
                                            <v-img :src="getProfilePictureUrl(userToEdit.profile_picture)"></v-img>
                                        </v-avatar>
                                        <div class="flex-grow-1">
                                            <span class="text-caption">Current profile picture</span>
                                        </div>
                                    </div>
                                </v-alert>
                            </v-col>

                            <!-- Skills Section (Admin only) -->
                            <v-col v-if="!isEditingSelf" cols="12">
                                <v-divider class="my-2"></v-divider>
                                <h3 class="text-subtitle-1 font-weight-bold mb-3 mt-4">Skills</h3>
                            </v-col>

                            <v-col v-if="!isEditingSelf" cols="12">
                                <v-combobox v-model="userToEdit.skills" label="Skills" outlined dense multiple chips
                                    small-chips deletable-chips prepend-inner-icon="mdi-star"
                                    hint="Type and press Enter to add skills" persistent-hint>
                                    <template v-slot:selection="{ item, index }">
                                        <v-chip small close @click:close="removeSkill(index)" class="skill-chip">
                                            {{ item }}
                                        </v-chip>
                                    </template>
                                </v-combobox>
                            </v-col>

                            <!-- Accomplishments Section (Admin only) -->
                            <v-col v-if="!isEditingSelf" cols="12">
                                <v-divider class="my-2"></v-divider>
                                <h3 class="text-subtitle-1 font-weight-bold mb-3 mt-4">Accomplishments</h3>
                            </v-col>

                            <v-col v-if="!isEditingSelf" cols="12">
                                <v-combobox v-model="userToEdit.accomplishments" label="Accomplishments" outlined dense
                                    multiple chips small-chips deletable-chips prepend-inner-icon="mdi-trophy"
                                    hint="Type and press Enter to add accomplishments" persistent-hint>
                                    <template v-slot:selection="{ item, index }">
                                        <v-chip small close @click:close="removeAccomplishment(index)" color="success"
                                            text-color="white">
                                            {{ item }}
                                        </v-chip>
                                    </template>
                                </v-combobox>
                            </v-col>

                            <!-- Company Access Section (Admin only) -->
                            <v-col v-if="!isEditingSelf" cols="12">
                                <v-divider class="my-2"></v-divider>
                                <h3 class="text-subtitle-1 font-weight-bold mb-3 mt-4">Company Access</h3>
                            </v-col>

                            <v-col v-if="!isEditingSelf" cols="12">
                                <v-autocomplete v-model="userCompanyAccess" label="Company Access"
                                    :items="companies()" item-text="name" item-value="id" outlined dense multiple
                                    chips small-chips deletable-chips prepend-inner-icon="mdi-domain"
                                    hint="Select companies this user can access" persistent-hint clearable
                                    return-object>
                                </v-autocomplete>
                            </v-col>

                            <!-- Manages Users Section (Admin only) -->
                            <v-col v-if="!isEditingSelf" cols="12">
                                <v-divider class="my-2"></v-divider>
                                <h3 class="text-subtitle-1 font-weight-bold mb-3 mt-4">Manages Users</h3>
                            </v-col>

                            <v-col v-if="!isEditingSelf" cols="12">
                                <v-autocomplete v-model="userToEdit.managed_user_ids" label="Manages Users"
                                    :items="manageableUsers" item-text="name" item-value="id" outlined dense multiple
                                    chips small-chips deletable-chips prepend-inner-icon="mdi-account-tie"
                                    hint="Select the users this user manages" persistent-hint clearable>
                                </v-autocomplete>
                            </v-col>
                        </v-row>
                    </v-form>
                </v-card-text>

                <v-divider></v-divider>

                <v-card-actions class="pa-4">
                    <v-spacer></v-spacer>
                    <v-btn text @click="closeEditDialog">Cancel</v-btn>
                    <v-btn color="primary" @click="saveUserChanges" :loading="saving">
                        <v-icon small class="mr-2">mdi-content-save</v-icon>
                        Save Changes
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Delete User Confirmation Dialog (Super Admin only) -->
        <v-dialog v-model="deleteUserDialog" max-width="600" persistent>
            <v-card class="delete-user-card">
                <v-card-title class="headline">Delete User</v-card-title>
                <v-card-text class="pa-6">
                    <p class="delete-user-message">Are you sure you want to delete this user?</p>
                    <p v-if="userToDelete" class="font-weight-medium mt-2 delete-user-name">{{ getUserFullName(userToDelete) }}</p>
                    <p class="text-caption grey--text mt-2 delete-user-caption">This action cannot be undone.</p>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="cancelDeleteUser" :disabled="deletingUser">Cancel</v-btn>
                    <v-btn color="red" dark @click="deleteUser" :loading="deletingUser" :disabled="deletingUser">
                        <v-icon left>mdi-delete</v-icon>
                        Delete
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Change Password Dialog (Admin only) -->
        <v-dialog v-model="changePasswordDialog" max-width="450" persistent>
            <v-card elevation="0">
                <v-card-title>
                    <span style="font-weight: 700; font-size: 1rem">Change password for {{ changePasswordUser ? getUserFullName(changePasswordUser) : '' }}</span>
                    <v-spacer />
                    <v-icon @click="closeChangePasswordDialog" small>mdi-close</v-icon>
                </v-card-title>
                <v-divider />
                <v-card-text class="mt-5">
                    <v-text-field label="New password" filled dense :append-icon="showChangePassword ? 'mdi-eye' : 'mdi-eye-off'" v-model="changePasswordValue"
                        @keyup.enter="changeUserPassword" @click:append="showChangePassword = !showChangePassword"
                        :type="showChangePassword ? 'text' : 'password'" />
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text @click="closeChangePasswordDialog">Cancel</v-btn>
                    <v-btn color="primary" outlined class="text-none" :loading="changingPassword" @click.stop="changeUserPassword">Update</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Task Creation Dialog -->
        <TaskCreationDialog v-model="taskCreationDialog" :users="availableUsersForTask" :companies="companies()"
            :default-assignees="selectedUserForTask" task-type="assign" @close="closeTaskCreationDialog"
            @tasks-created="onTasksCreated" />

        <!-- Create User Dialog -->
        <v-dialog v-model="createUserDialog" max-width="800" persistent scrollable>
            <v-card>
                <v-card-title class="primary white--text">
                    <v-icon dark class="mr-2">mdi-account-plus</v-icon>
                    <span class="text-h5">Create New User</span>
                    <v-spacer></v-spacer>
                    <v-btn icon dark @click="closeCreateUserDialog">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>

                <v-card-text class="pa-6">
                    <v-form ref="createUserForm" v-model="createUserFormValid">
                        <v-row>
                            <!-- Basic Information -->
                            <v-col cols="12">
                                <h3 class="text-subtitle-1 font-weight-bold mb-3">Basic Information</h3>
                            </v-col>

                            <v-col cols="12" md="6">
                                <v-text-field v-model="newUser.first_name" label="First Name" outlined dense
                                    prepend-inner-icon="mdi-account" :rules="[]"></v-text-field>
                            </v-col>

                            <v-col cols="12" md="6">
                                <v-text-field v-model="newUser.last_name" label="Last Name" outlined dense
                                    :rules="[]"></v-text-field>
                            </v-col>

                            <v-col cols="12" md="6">
                                <v-text-field v-model="newUser.name" label="Display Name *" outlined dense
                                    prepend-inner-icon="mdi-card-account-details" :rules="nameRules"
                                    required></v-text-field>
                            </v-col>

                            <v-col cols="12" md="6">
                                <v-text-field v-model="newUser.email" label="Email *" outlined dense type="email"
                                    prepend-inner-icon="mdi-email" :rules="emailRules" required></v-text-field>
                            </v-col>

                            <v-col cols="12" md="6">
                                <v-text-field v-model="newUser.secondary_email" label="Secondary Email" outlined dense type="email"
                                    prepend-inner-icon="mdi-email-outline"></v-text-field>
                            </v-col>

                            <v-col cols="12" md="6">
                                <v-text-field v-model="newUser.password" label="Password *" outlined dense
                                    :type="showPassword ? 'text' : 'password'" prepend-inner-icon="mdi-lock"
                                    :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                                    @click:append="showPassword = !showPassword" :rules="passwordRules"
                                    required></v-text-field>
                            </v-col>

                            <v-col cols="12" md="6">
                                <v-text-field v-model="newUser.phone" label="Phone" outlined dense
                                    prepend-inner-icon="mdi-phone"></v-text-field>
                            </v-col>

                            <v-col cols="12" md="6">
                                <v-select v-model="newUser.role_ids" label="Roles / Teams *" :items="get_roles" item-text="name"
                                    item-value="id" outlined dense prepend-inner-icon="mdi-shield-account"
                                    :rules="roleRules" multiple chips small-chips deletable-chips required></v-select>
                            </v-col>

                            <v-col cols="12" md="6">
                                <v-text-field v-model="newUser.position_name" label="Position" outlined dense
                                    prepend-inner-icon="mdi-briefcase"></v-text-field>
                            </v-col>

                            <v-col cols="12" md="6">
                                <v-text-field v-model="newUser.location" label="Location" outlined dense
                                    prepend-inner-icon="mdi-map-marker"></v-text-field>
                            </v-col>

                            <v-col cols="12" md="6">
                                <v-text-field v-model="newUser.joined_date" label="Joined Date" outlined dense
                                    type="date" prepend-inner-icon="mdi-calendar"></v-text-field>
                            </v-col>

                            <!-- Skills Section -->
                            <v-col cols="12">
                                <v-divider class="my-2"></v-divider>
                                <h3 class="text-subtitle-1 font-weight-bold mb-3 mt-4">Skills</h3>
                            </v-col>

                            <v-col cols="12">
                                <v-combobox v-model="newUser.skills" label="Skills" outlined dense multiple chips
                                    small-chips deletable-chips prepend-inner-icon="mdi-star"
                                    hint="Type and press Enter to add skills" persistent-hint>
                                    <template v-slot:selection="{ item, index }">
                                        <v-chip small close @click:close="removeNewUserSkill(index)" class="skill-chip">
                                            {{ item }}
                                        </v-chip>
                                    </template>
                                </v-combobox>
                            </v-col>

                            <!-- Accomplishments Section -->
                            <v-col cols="12">
                                <v-divider class="my-2"></v-divider>
                                <h3 class="text-subtitle-1 font-weight-bold mb-3 mt-4">Accomplishments</h3>
                            </v-col>

                            <v-col cols="12">
                                <v-combobox v-model="newUser.accomplishments" label="Accomplishments" outlined dense
                                    multiple chips small-chips deletable-chips prepend-inner-icon="mdi-trophy"
                                    hint="Type and press Enter to add accomplishments" persistent-hint>
                                    <template v-slot:selection="{ item, index }">
                                        <v-chip small close @click:close="removeNewUserAccomplishment(index)"
                                            color="success" text-color="white">
                                            {{ item }}
                                        </v-chip>
                                    </template>
                                </v-combobox>
                            </v-col>

                            <!-- Company Access Section -->
                            <v-col cols="12">
                                <v-divider class="my-2"></v-divider>
                                <h3 class="text-subtitle-1 font-weight-bold mb-3 mt-4">Company Access</h3>
                            </v-col>

                            <v-col cols="12">
                                <v-autocomplete v-model="newUserCompanyAccess" label="Company Access"
                                    :items="companies()" item-text="name" item-value="id" outlined dense multiple
                                    chips small-chips deletable-chips prepend-inner-icon="mdi-domain"
                                    hint="Select companies this user can access" persistent-hint clearable
                                    return-object>
                                </v-autocomplete>
                            </v-col>
                        </v-row>
                    </v-form>
                </v-card-text>

                <v-divider></v-divider>

                <v-card-actions class="pa-4">
                    <v-spacer></v-spacer>
                    <v-btn text @click="closeCreateUserDialog">Cancel</v-btn>
                    <v-btn color="primary" @click="createUser" :loading="creating" :disabled="!createUserFormValid">
                        <v-icon small class="mr-2">mdi-account-plus</v-icon>
                        Create User
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import TaskCreationDialog from "../modules/TaskCreationDialog.vue";
import UserDetailsDialog from "../modules/UserDetailsDialog.vue";

export default {
    name: "UserDirectoryComponent",
    components: {
        TaskCreationDialog,
        UserDetailsDialog,
    },
    inject: ['users', 'updateUser', 'addUser', 'removeUser', 'refreshUsers', 'companies', 'refreshCompanies'],
    data() {
        return {
            loading: false,
            refreshing: false,
            saving: false,
            uploading: false,
            creating: false,
            userDetailsDialog: false,
            selectedUser: null,
            editUserDialog: false,
            createUserDialog: false,
            taskCreationDialog: false,
            // Filter state
            selectedCompany: null,
            selectedRole: null,
            // Form validation
            createUserFormValid: false,
            showPassword: false,
            userToEdit: {
                id: null,
                name: null,
                first_name: null,
                last_name: null,
                email: null,
                secondary_email: null,
                role_ids: [],
                position_name: null,
                location: null,
                phone: null,
                joined_date: null,
                skills: [],
                accomplishments: [],
                profile_picture: null,
                company_access: [],
                managed_user_ids: [],
            },
            newUser: {
                name: '',
                first_name: '',
                last_name: '',
                email: '',
                secondary_email: '',
                password: '',
                role_ids: [],
                position_name: '',
                location: '',
                phone: '',
                joined_date: '',
                skills: [],
                accomplishments: [],
                profile_picture: null,
                access: [],
            },
            skillInput: '',
            accomplishmentInput: '',
            profilePictureFile: null,
            // Task-related data
            userTasks: [],
            loadingTasks: false,
            taskError: null,
            // User statistics
            selectedUserStats: {
                total_tasks: 0,
                in_progress_tasks: 0,
                completed_tasks: 0,
                overdue_tasks: 0,
            },
            // Validation rules
            nameRules: [
                v => !!v || 'Display name is required',
                v => (v && v.length >= 2) || 'Display name must be at least 2 characters',
            ],
            emailRules: [
                v => !!v || 'Email is required',
                v => /.+@.+\..+/.test(v) || 'Email must be valid',
            ],
            passwordRules: [
                v => !!v || 'Password is required',
                v => (v && v.length >= 6) || 'Password must be at least 6 characters',
            ],
            roleRules: [
                v => (Array.isArray(v) && v.length > 0) || 'At least one role is required',
            ],
            // Delete user dialog state (super admin only)
            deleteUserDialog: false,
            loggingOutUserId: null,
            userToDelete: null,
            deletingUser: false,
            // Change password dialog state
            changePasswordDialog: false,
            changePasswordUser: null,
            changePasswordValue: null,
            showChangePassword: false,
            changingPassword: false,
            // Impersonation
            impersonatingUserId: null,
        };
    },
    async mounted() {
        document.title = "DG Tasks - Employee Directory";
        // Users are already loaded in DefaultLayout, just load companies
        this.loadCompanies();
    },
    watch: {
        users: {
            handler() {
                // Reload companies when users change
                this.loadCompanies();
            },
            deep: true
        }
    },
    computed: {
        ...mapGetters({
            get_user: "auth/get_user",
            get_roles: "auth/get_roles",
            get_search_query: "users/get_search_query",
            is_impersonating: "auth/is_impersonating",
        }),
        sortedRoles() {
            return [...(this.get_roles || [])].sort((a, b) => a.id - b.id);
        },
        manageableUsers() {
            // A user can't manage themselves
            return this.users().filter(u => u.id !== this.userToEdit.id);
        },
        isAdmin() {
            // Check if user is Super Admin (1) or Admin (2)
            return this.get_user && [1, 2, 11].includes(this.get_user.role_id);
        },
        canAddEmployee() {
            if (!this.get_user) return false;
            const allowedIds = [177, 42];
            if (allowedIds.includes(this.get_user.id)) return true;
            const ceoEmail = process.env.VUE_APP_CEO_EMAIL;
            const cooEmail = process.env.VUE_APP_COO_EMAIL;
            const userEmail = this.get_user.email?.toLowerCase();
            return !!userEmail && (
                (ceoEmail && userEmail === ceoEmail.toLowerCase()) ||
                (cooEmail && userEmail === cooEmail.toLowerCase())
            );
        },
        isSuperAdmin() {
            return this.get_user && this.get_user.role_id === 1;
        },
        isEditingSelf() {
            return this.userToEdit.id === this.get_user?.id;
        },
        apiUrl() {
            return process.env.VUE_APP_API_URL || '';
        },
        searchQuery: {
            get() {
                return this.get_search_query || "";
            },
            set(value) {
                this.$store.commit("users/set_search_query", value);
            },
        },
        filteredUsers() {
            let filtered = this.users();

            // Apply company filter
            if (this.selectedCompany !== null) {
                filtered = filtered.filter((user) => {
                    if (user.access && Array.isArray(user.access)) {
                        return user.access.some(access => {
                            return access.company_id === this.selectedCompany ||
                                (access.company && access.company.id === this.selectedCompany);
                        });
                    }
                    return false;
                });
            }

            // Apply role filter (using role_members for multi-role support)
            if (this.selectedRole !== null) {
                filtered = filtered.filter((user) => {
                    if (user.role_members && user.role_members.length > 0) {
                        return user.role_members.some(m => m.user_role_id === this.selectedRole);
                    }
                    // Fallback to old role_id for users without role_members
                    return user.role_id === this.selectedRole;
                });
            }

            // Apply search query
            if (this.searchQuery) {
                const query = this.searchQuery.toLowerCase();
                filtered = filtered.filter((user) => {
                    // Search by name
                    const fullName = this.getUserFullName(user).toLowerCase();
                    if (fullName.includes(query)) return true;

                    // Search by email
                    if (user.email?.toLowerCase().includes(query)) return true;

                    // Search by position
                    if (user.position_name?.toLowerCase().includes(query)) return true;
                    if (user.role?.name?.toLowerCase().includes(query)) return true;

                    // Search by team/role names from role_members
                    if (user.role_members && user.role_members.length > 0) {
                        const roleMatch = user.role_members.some(m =>
                            m.role?.name?.toLowerCase().includes(query)
                        );
                        if (roleMatch) return true;
                    }

                    // Search by skills
                    if (user.skills && Array.isArray(user.skills)) {
                        const skillMatch = user.skills.some((skill) =>
                            skill.toLowerCase().includes(query)
                        );
                        if (skillMatch) return true;
                    }

                    // Search by location
                    if (user.location?.toLowerCase().includes(query)) return true;

                    return false;
                });
            }

            return filtered;
        },
        groupedUsers() {
            const grouped = {};

            this.filteredUsers.forEach((user) => {
                // Group by role_members (multi-role support)
                if (user.role_members && user.role_members.length > 0) {
                    user.role_members.forEach((member) => {
                        const groupKey = member.role?.name || 'Other';
                        if (!grouped[groupKey]) {
                            grouped[groupKey] = [];
                        }
                        // Avoid duplicates if user is already in this group
                        if (!grouped[groupKey].some(u => u.id === user.id)) {
                            grouped[groupKey].push(user);
                        }
                    });
                } else {
                    // Fallback: use old role.name or position
                    const groupKey = user.role?.name || user.position_name || 'Other';
                    if (!grouped[groupKey]) {
                        grouped[groupKey] = [];
                    }
                    grouped[groupKey].push(user);
                }
            });

            // Re-order groups by role ID using sortedRoles so sections appear in
            // the same order as the roles table (id ascending)
            const sorted = {};
            this.sortedRoles.forEach(role => {
                if (grouped[role.name]) {
                    sorted[role.name] = grouped[role.name];
                }
            });
            // Append any leftover keys not matched to a known role (e.g. 'Other')
            Object.keys(grouped).forEach(key => {
                if (!sorted[key]) {
                    sorted[key] = grouped[key];
                }
            });

            return sorted;
        },
        // Helper computed properties for filters
        hasActiveFilters() {
            return this.selectedCompany !== null || this.selectedRole !== null;
        },
        totalFilteredUsers() {
            return this.filteredUsers.length;
        },
        totalEmployeeCount() {
            const allUsers = this.users() || [];
            const uniqueNames = new Set(allUsers.map(u => (u.name || '').trim().toLowerCase()).filter(Boolean));
            return uniqueNames.size;
        },
        availableUsersForTask() {
            // Return all users for task assignment
            return this.users();
        },
        selectedUserForTask() {
            // Return the selected user's ID as an array for default assignees
            return this.selectedUser ? [this.selectedUser.id] : [];
        },
        // Computed property for company access to handle object/ID mapping
        userCompanyAccess: {
            get() {
                // Convert IDs to full company objects for v-autocomplete
                if (!this.userToEdit.company_access || !Array.isArray(this.userToEdit.company_access)) {
                    return [];
                }
                return this.userToEdit.company_access
                    .map(id => this.companies().find(company => company.id === id))
                    .filter(company => company); // Remove any undefined values
            },
            set(selectedCompanies) {
                // Convert company objects back to IDs for storage
                this.userToEdit.company_access = selectedCompanies.map(company => company.id);
            }
        },
        // Computed property for new user company access
        newUserCompanyAccess: {
            get() {
                // Convert access array to company objects for v-autocomplete
                if (!this.newUser.access || !Array.isArray(this.newUser.access)) {
                    return [];
                }
                return this.newUser.access
                    .map(id => this.companies().find(company => company.id === id))
                    .filter(company => company); // Remove any undefined values
            },
            set(selectedCompanies) {
                // Convert company objects back to IDs for storage
                this.newUser.access = selectedCompanies.map(company => company.id);
            }
        },
    },
    methods: {
        async loadUsers() {
            this.loading = true;
            try {
                await this.refreshUsers();
            } catch (error) {
                console.error("Error loading users:", error);
                this.$toast.error("Failed to load users");
            } finally {
                this.loading = false;
            }
        },
        async loadCompanies() {
            try {
                // Use the injected companies provider instead of manual loading
                await this.refreshCompanies();
            } catch (error) {
                console.error("Error loading companies:", error);
            }
        },
        async loadUserTasks(userId) {
            // Check permissions for users 2 and 4
            if (!this.canViewUserTasks(userId)) {
                this.loadingTasks = false;
                this.taskError = null;
                this.userTasks = [];
                return;
            }

            this.loadingTasks = true;
            this.taskError = null;
            this.userTasks = [];

            try {
                const response = await this.$axios.get(`api/tasks/get_tasks_by_assignee/${userId}`);

                // Check different possible response structures
                if (response.data) {
                    if (Array.isArray(response.data)) {
                        // Direct array response
                        this.userTasks = response.data;
                    } else if (response.data.data && Array.isArray(response.data.data)) {
                        // Nested data array
                        this.userTasks = response.data.data;
                    } else if (response.data.response === true && response.data.data) {
                        // Success response with data
                        this.userTasks = Array.isArray(response.data.data) ? response.data.data : [];
                    } else {
                        // Unknown structure
                        this.taskError = 'Unexpected API response format';
                        this.userTasks = [];
                    }
                } else {
                    this.taskError = 'No data received from API';
                    this.userTasks = [];
                }

            } catch (error) {
                console.error("Error loading user tasks:", error);
                this.taskError = `Failed to load user tasks: ${error.message}`;
                this.userTasks = [];
            } finally {
                this.loadingTasks = false;
            }
        },
        async loadUserStats(userId) {
            // Check permissions for users 2 and 4
            if (!this.canViewUserTasks(userId)) {
                this.selectedUserStats = {
                    total_tasks: 0,
                    in_progress_tasks: 0,
                    completed_tasks: 0,
                    overdue_tasks: 0,
                };
                return;
            }

            try {
                this.selectedUserStats = await this.getUserTasksOverview(userId);
            } catch (error) {
                console.error("Error loading user statistics:", error);
                // Reset to default values on error
                this.selectedUserStats = {
                    total_tasks: 0,
                    in_progress_tasks: 0,
                    completed_tasks: 0,
                    overdue_tasks: 0,
                };
            }
        },
        isCurrentUser(user) {
            return user && user.id === this.get_user?.id;
        },
        canImpersonate(user) {
            if (!user || !this.isAdmin) return false;
            if (this.is_impersonating) return false; // already impersonating someone
            if (this.isCurrentUser(user)) return false;
            // Only super admin (1) can impersonate another super admin
            if (user.role_id === 1 && !this.isSuperAdmin) return false;
            return true;
        },
        async impersonateUser(user) {
            if (!this.canImpersonate(user)) return;
            const confirmed = window.confirm(
                `Take over ${this.getUserFullName(user)}'s account? You will be able to exit at any time from the header.`
            );
            if (!confirmed) return;

            this.impersonatingUserId = user.id;
            try {
                const response = await this.$axios.post(`api/impersonate/${user.id}`);
                const { data } = response;
                if (!data || !data.response) {
                    this.$toast.error(data?.message || 'Failed to start impersonation');
                    return;
                }

                // Save current admin context so we can restore after exiting impersonation
                const currentAdmin = {
                    user: this.get_user,
                    token: this.$store.getters['auth/get_token'],
                };
                this.$store.dispatch('auth/set_impersonator', currentAdmin);

                // Switch to the impersonated user's session
                this.$store.dispatch('auth/set_user', data.user);
                this.$store.dispatch('auth/set_token', data.token);
                if (data.roles) this.$store.dispatch('auth/set_roles', data.roles);
                if (data.priorities) this.$store.dispatch('tasks/set_priorities', data.priorities);
                if (data.statuses) this.$store.dispatch('tasks/set_statuses', data.statuses);
                if (data.meeting_statuses) {
                    this.$store.dispatch('meetings/set_meeting_statuses', data.meeting_statuses);
                }
                if (data.project_statuses) {
                    this.$store.dispatch('projects/set_project_statuses', data.project_statuses);
                }

                this.$toast.success(`You are now impersonating ${data.user.name}`);
                // Reload so all providers/data refetch under the new identity
                this.$router.push({ name: 'Dashboard' }).catch(() => { });
                setTimeout(() => window.location.reload(), 100);
            } catch (error) {
                console.error('Impersonate error:', error);
                this.$toast.error('Failed to start impersonation');
            } finally {
                this.impersonatingUserId = null;
            }
        },
        getUserFullName(user) {
            if (user.first_name || user.last_name) {
                return `${user.first_name || ""} ${user.last_name || ""}`.trim();
            }
            return user.name || "No Name";
        },
        getUserInitials(user) {
            if (user.first_name && user.last_name) {
                return `${user.first_name[0]}${user.last_name[0]}`.toUpperCase();
            }
            if (user.name) {
                const parts = user.name.split(" ");
                if (parts.length >= 2) {
                    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
                }
                return user.name[0].toUpperCase();
            }
            return "NN";
        },
        getDisplaySkills(skills) {
            if (!skills || !Array.isArray(skills)) return [];
            return skills.slice(0, 3);
        },
        getProfilePictureUrl(profilePicture) {
            if (!profilePicture) return null;

            // If it's already a full URL, return as is
            if (profilePicture.startsWith('http://') || profilePicture.startsWith('https://')) {
                return profilePicture;
            }

            // Convert old /media/ format to new /storage/ format
            let picturePath = profilePicture;
            if (picturePath.startsWith('/media/')) {
                picturePath = picturePath.replace('/media/', '/storage/');
            }

            // Otherwise, construct the full URL with the API base URL
            const apiUrl = this.apiUrl.endsWith('/') ? this.apiUrl.slice(0, -1) : this.apiUrl;
            picturePath = picturePath.startsWith('/') ? picturePath : `/${picturePath}`;
            return `${apiUrl}${picturePath}`;
        },
        formatDate(date) {
            if (!date) return "N/A";
            return this.$date(date).format("MMMM DD, YYYY");
        },
        viewUserKpis(user) {
            if (!user || !user.id) return;
            this.$router.push({ name: 'UserKpis', params: { userId: user.id } });
        },
        viewUserDetails(user) {
            this.selectedUser = user;
            this.userDetailsDialog = true;
            // Load tasks for the selected user
            this.loadUserTasks(user.id);
            // Load user statistics
            this.loadUserStats(user.id);
        },
        openEditDialog(user) {
            if (!user || !user.id) {
                this.$toast.error('Invalid user data. Please try again.');
                return;
            }

            this.userToEdit = {
                id: user.id,
                name: user.name,
                first_name: user.first_name || '',
                last_name: user.last_name || '',
                email: user.email || '',
                secondary_email: user.secondary_email || '',
                role_ids: user.role_members && user.role_members.length
                    ? user.role_members.map(m => m.user_role_id)
                    : (user.role_id ? [user.role_id] : []),
                position_name: user.position_name || '',
                location: user.location || '',
                phone: user.phone || '',
                joined_date: user.joined_date || '',
                skills: user.skills ? [...user.skills] : [],
                accomplishments: user.accomplishments ? [...user.accomplishments] : [],
                profile_picture: user.profile_picture || '',
                company_access: user.access ? user.access.map(access => access.company_id || access.company?.id).filter(id => id) : [],
                managed_user_ids: user.managed_users ? user.managed_users.map(m => m.managed_user_id) : [],
            };
            this.editUserDialog = true;
        },
        openEditDialogFromDetails() {
            if (!this.selectedUser) {
                this.$toast.error('No user selected. Please try again.');
                return;
            }
            this.openEditDialog(this.selectedUser);
            this.closeUserDetailsDialog();
        },
        openCreateUserDialog() {
            this.createUserDialog = true;
        },
        closeCreateUserDialog() {
            this.createUserDialog = false;
            this.createUserFormValid = false;
            this.showPassword = false;
            this.newUser = {
                name: '',
                first_name: '',
                last_name: '',
                email: '',
                secondary_email: '',
                password: '',
                role_ids: [],
                position_name: '',
                location: '',
                phone: '',
                joined_date: '',
                skills: [],
                accomplishments: [],
                profile_picture: null,
                access: [],
            };
            // Reset form validation
            if (this.$refs.createUserForm) {
                this.$refs.createUserForm.resetValidation();
            }
        },
        openTaskCreationDialog() {
            this.taskCreationDialog = true;
        },
        closeTaskCreationDialog() {
            this.taskCreationDialog = false;
        },
        async onTasksCreated(tasks) {
            // Close the task creation dialog
            this.closeTaskCreationDialog();

            // Show success message
            const taskCount = Array.isArray(tasks) ? tasks.length : 1;
            this.$toast.success(`${taskCount} task${taskCount > 1 ? 's' : ''} assigned to ${this.getUserFullName(this.selectedUser)}`);

            // Refresh the user's tasks while keeping the dialog open
            if (this.selectedUser) {
                await this.loadUserTasks(this.selectedUser.id);
                await this.loadUserStats(this.selectedUser.id);
            }
        },
        closeEditDialog() {
            this.editUserDialog = false;
            this.profilePictureFile = null;
            this.userToEdit = {
                id: null,
                name: null,
                first_name: null,
                last_name: null,
                email: null,
                secondary_email: null,
                role_ids: [],
                position_name: null,
                location: null,
                phone: null,
                joined_date: null,
                skills: [],
                accomplishments: [],
                profile_picture: null,
                company_access: [],
                managed_user_ids: [],
            };
        },
        removeSkill(index) {
            this.userToEdit.skills.splice(index, 1);
        },
        removeAccomplishment(index) {
            this.userToEdit.accomplishments.splice(index, 1);
        },
        removeNewUserSkill(index) {
            this.newUser.skills.splice(index, 1);
        },
        removeNewUserAccomplishment(index) {
            this.newUser.accomplishments.splice(index, 1);
        },
        closeUserDetailsDialog() {
            this.userDetailsDialog = false;
            this.selectedUser = null;
            this.userTasks = [];
            this.loadingTasks = false;
            this.taskError = null;
            // Reset user statistics
            this.selectedUserStats = {
                total_tasks: 0,
                in_progress_tasks: 0,
                completed_tasks: 0,
                overdue_tasks: 0,
            };
            // Close task creation dialog if open
            this.taskCreationDialog = false;
        },
        async uploadProfilePicture() {
            // Check if a file is actually selected
            if (!this.profilePictureFile) {
                console.log('No file selected for upload');
                return;
            }

            // Validate file type
            const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
            if (!allowedTypes.includes(this.profilePictureFile.type)) {
                this.$toast.error('Please select a valid image file (JPEG, PNG, or GIF)');
                this.profilePictureFile = null;
                return;
            }

            // Validate file size (max 5MB)
            const maxSize = 5 * 1024 * 1024; // 5MB in bytes
            if (this.profilePictureFile.size > maxSize) {
                this.$toast.error('File size must be less than 5MB');
                this.profilePictureFile = null;
                return;
            }

            // Validate that we have a valid user ID
            if (!this.userToEdit.id) {
                this.$toast.error('Invalid user data. Please close and reopen the edit dialog.');
                this.profilePictureFile = null;
                return;
            }

            this.uploading = true;
            try {
                const formData = new FormData();
                console.log('Uploading file:', this.profilePictureFile);
                formData.append('profile_picture', this.profilePictureFile);
                formData.append('user_id', this.userToEdit.id);

                const response = await this.$axios.post(
                    'api/users/upload_profile_picture',
                    formData,
                    {
                        headers: {
                            'Content-Type': 'multipart/form-data'
                        }
                    }
                );

                if (response.data.response) {
                    this.userToEdit.profile_picture = response.data.data.profile_picture;
                    this.$toast.success('Profile picture uploaded successfully');
                } else {
                    this.$toast.error(response.data.message || 'Failed to upload profile picture');
                }
            } catch (error) {
                console.error('Error uploading profile picture:', error);
                if (error.response && error.response.data && error.response.data.message) {
                    this.$toast.error(error.response.data.message);
                } else {
                    this.$toast.error('Failed to upload profile picture');
                }
            } finally {
                this.uploading = false;
            }
        },
        async saveUserChanges() {
            // Validate that we have a valid user ID
            if (!this.userToEdit.id) {
                this.$toast.error('Invalid user data. Please close and reopen the edit dialog.');
                return;
            }

            this.saving = true;

            // Store the user ID before any potential state changes
            const userId = this.userToEdit.id;

            // Store original user data for potential rollback
            const originalUser = this.users().find(u => u.id === userId);
            const originalUserCopy = originalUser ? { ...originalUser } : null;

            if (!originalUser) {
                this.$toast.error('User not found. Please refresh and try again.');
                this.saving = false;
                return;
            }

            try {
                // Prepare payload with proper JSON formatting
                const payload = {
                    first_name: this.userToEdit.first_name,
                    last_name: this.userToEdit.last_name,
                    name: this.userToEdit.name,
                    email: this.userToEdit.email,
                    secondary_email: this.userToEdit.secondary_email,
                    role_ids: this.userToEdit.role_ids,
                    role_id: this.userToEdit.role_ids[0] || null,
                    position_name: this.userToEdit.position_name,
                    location: this.userToEdit.location,
                    phone: this.userToEdit.phone,
                    joined_date: this.userToEdit.joined_date,
                    skills: this.userToEdit.skills,
                    accomplishments: this.userToEdit.accomplishments,
                    profile_picture: this.userToEdit.profile_picture,
                    company_access: this.userToEdit.company_access,
                    managed_user_ids: this.userToEdit.managed_user_ids,
                };

                // Optimistically update the user
                const updatedUser = {
                    ...originalUser,
                    ...payload,
                    // Map company_access to access format for UI consistency
                    access: this.userToEdit.company_access.map(companyId => {
                        const company = this.companies().find(c => c.id === companyId);
                        return {
                            company_id: companyId,
                            company: company || { id: companyId, name: 'Unknown' }
                        };
                    }),
                    // Update role reference if role changed
                    role: this.get_roles.find(r => r.id === this.userToEdit.role_ids[0]) || originalUser.role,
                    role_members: this.userToEdit.role_ids.map(id => ({ user_role_id: id, role: this.get_roles.find(r => r.id === id) })),
                    managed_users: this.userToEdit.managed_user_ids.map(id => ({ manager_id: userId, managed_user_id: id })),
                };

                // Update the user optimistically
                this.updateUser(updatedUser);

                // Update selectedUser if details dialog was open
                if (this.selectedUser && this.selectedUser.id === userId) {
                    this.selectedUser = updatedUser;
                }

                // Show success message immediately
                this.$toast.success('User information updated successfully');

                // Close dialog immediately
                this.closeEditDialog();

                // Make API call in the background using stored userId
                this.$axios.post(
                    `api/users/update_user_directory/${userId}`,
                    payload
                ).then(() => {
                    // Silently refresh users in background to sync with server
                    this.refreshUsers();
                }).catch((error) => {
                    console.error('Error updating user on server:', error);

                    // Rollback optimistic update on API failure
                    if (originalUserCopy) {
                        this.updateUser(originalUserCopy);
                        if (this.selectedUser && this.selectedUser.id === originalUserCopy.id) {
                            this.selectedUser = originalUserCopy;
                        }
                    }

                    this.$toast.error('Failed to sync user changes with server. Changes have been reverted.');
                });

            } catch (error) {
                console.error('Error in optimistic update:', error);

                // Rollback optimistic update
                if (originalUserCopy) {
                    this.updateUser(originalUserCopy);
                    if (this.selectedUser && this.selectedUser.id === originalUserCopy.id) {
                        this.selectedUser = originalUserCopy;
                    }
                }

                this.$toast.error('Failed to update user information');
            } finally {
                this.saving = false;
            }
        },
        async createUser() {
            // Validate form first
            if (!this.$refs.createUserForm.validate()) {
                this.$toast.error('Please fill in all required fields correctly');
                return;
            }

            this.creating = true;

            try {
                // Prepare payload according to backend validation
                const payload = {
                    role_ids: this.newUser.role_ids,
                    access: this.newUser.access,
                    name: this.newUser.name,
                    first_name: this.newUser.first_name || null,
                    last_name: this.newUser.last_name || null,
                    position_name: this.newUser.position_name || null,
                    location: this.newUser.location || null,
                    skills: this.newUser.skills,
                    accomplishments: this.newUser.accomplishments,
                    joined_date: this.newUser.joined_date || null,
                    profile_picture: this.newUser.profile_picture || null,
                    phone: this.newUser.phone || null,
                    email: this.newUser.email,
                    secondary_email: this.newUser.secondary_email || null,
                    password: this.newUser.password,
                };

                // Create optimistic user object with temporary ID
                const tempId = Date.now(); // Use timestamp as temporary ID
                const optimisticUser = {
                    id: tempId,
                    ...payload,
                    // Map access to the expected format for UI
                    access: this.newUser.access.map(companyId => {
                        const company = this.companies().find(c => c.id === companyId);
                        return {
                            company_id: companyId,
                            company: company || { id: companyId, name: 'Unknown' }
                        };
                    }),
                    // Add role reference
                    role: this.get_roles.find(r => r.id === this.newUser.role_ids[0]),
                    // Add timestamps
                    created_at: new Date().toISOString(),
                    updated_at: new Date().toISOString(),
                };

                // Optimistically add user
                this.addUser(optimisticUser);

                // Show success message immediately
                this.$toast.success('User created successfully');

                // Close dialog immediately
                this.closeCreateUserDialog();

                // Make API call in the background
                this.$axios.post('api/users/create_user', payload).then((response) => {
                    if (response.data.response && response.data.data) {
                        // Replace optimistic user with real user data from server
                        const realUser = response.data.data;
                        this.removeUser(tempId);
                        this.addUser(realUser);

                        // Refresh users to ensure data consistency
                        this.refreshUsers();
                    } else {
                        // Remove optimistic user and show error
                        this.removeUser(tempId);
                        this.$toast.error(response.data.message || 'Failed to create user');
                    }
                }).catch((error) => {
                    console.error('Error creating user on server:', error);

                    // Remove optimistic user on failure
                    this.removeUser(tempId);

                    // Handle validation errors from backend
                    if (error.response && error.response.status === 422 && error.response.data.errors) {
                        const errors = error.response.data.errors;
                        let errorMessage = 'Validation failed:\n';
                        Object.keys(errors).forEach(field => {
                            errorMessage += `${field}: ${errors[field].join(', ')}\n`;
                        });
                        this.$toast.error(errorMessage);
                    } else if (error.response && error.response.data && error.response.data.message) {
                        this.$toast.error(error.response.data.message);
                    } else {
                        this.$toast.error('Failed to create user');
                    }
                });

            } catch (error) {
                console.error('Error in optimistic user creation:', error);
                this.$toast.error('Failed to create user');
            } finally {
                this.creating = false;
            }
        },
        // Filter helper methods
        getNoResultsMessage() {
            if (this.searchQuery && this.hasActiveFilters) {
                return `No users found matching "${this.searchQuery}" with the selected filters. Try clearing the filters to find more users.`;
            } else if (this.searchQuery) {
                return `No users found matching "${this.searchQuery}". Try adjusting your search criteria.`;
            } else if (this.hasActiveFilters) {
                return 'No users found with the selected filters. Try selecting different filters.';
            } else {
                return 'Try adjusting your search criteria.';
            }
        },
        clearAllFilters() {
            this.selectedCompany = null;
            this.selectedRole = null;
            this.searchQuery = '';
        },
        canViewUserTasks(userId) {
            const currentUser = this.get_user;
            if (!currentUser) return false;

            const currentUserId = currentUser.id;
            const restrictedUserIds = [2, 4];

            // If the target user is not in the restricted list, everyone can see their tasks
            if (!restrictedUserIds.includes(userId)) {
                return true;
            }

            // If viewing user 2's tasks
            if (userId === 2) {
                // Only user 2 can see their own tasks
                return currentUserId === 2;
            }

            // If viewing user 4's tasks  
            if (userId === 4) {
                // User 2 can see user 4's tasks, and user 4 can see their own tasks
                return currentUserId === 2 || currentUserId === 4;
            }

            return false;
        },
        async refreshData() {
            this.refreshing = true;
            try {
                await Promise.all([
                    this.refreshUsers(),
                    this.refreshCompanies(),
                ]);
                this.$toast.success('Data refreshed successfully');
            } catch (error) {
                console.error('Error refreshing data:', error);
                this.$toast.error('Failed to refresh data');
            } finally {
                this.refreshing = false;
            }
        },
        // --------- Super admin: delete user ---------
        confirmDeleteUser(user) {
            this.userToDelete = user;
            this.deleteUserDialog = true;
        },
        cancelDeleteUser() {
            this.deleteUserDialog = false;
            this.userToDelete = null;
        },
        async deleteUser() {
            if (!this.userToDelete || !this.userToDelete.id) return;

            this.deletingUser = true;
            try {
                const response = await this.$axios.delete(`api/users/delete_user/${this.userToDelete.id}`);
                if (response.data && (response.data.response || response.data.success)) {
                    this.$toast.success(response.data.message || `${this.getUserFullName(this.userToDelete)} deleted`);
                    this.deleteUserDialog = false;
                    this.userToDelete = null;
                    // Refresh users and companies to reflect removal
                    await this.refreshUsers();
                    await this.refreshCompanies();
                } else {
                    this.$toast.error(response.data.message || 'Failed to delete user');
                }
            } catch (error) {
                console.error('Error deleting user:', error);
                this.$toast.error(error.response?.data?.message || 'Failed to delete user');
            } finally {
                this.deletingUser = false;
            }
        },
        async logoutUserAllSessions(user) {
            if (!user || !user.id || this.loggingOutUserId) return;
            if (!confirm(`Log ${this.getUserFullName(user)} out of all sessions? Their access will be revoked until they sign in again.`)) return;

            this.loggingOutUserId = user.id;
            try {
                const response = await this.$axios.post(`api/users/logout_all_sessions/${user.id}`);
                if (response.data && response.data.response) {
                    this.$toast.success(`${this.getUserFullName(user)} logged out from all sessions`);
                } else {
                    this.$toast.error(response.data?.message || 'Failed to log out user');
                }
            } catch (error) {
                console.error('Error logging out user:', error);
                this.$toast.error(error.response?.data?.message || 'Failed to log out user');
            } finally {
                this.loggingOutUserId = null;
            }
        },
        // Change password methods
        openChangePasswordDialog(user) {
            this.changePasswordUser = user;
            this.changePasswordValue = null;
            this.showChangePassword = false;
            this.changePasswordDialog = true;
        },
        closeChangePasswordDialog() {
            this.changePasswordDialog = false;
            this.changePasswordUser = null;
            this.changePasswordValue = null;
            this.showChangePassword = false;
        },
        openChangePasswordFromDetails() {
            if (!this.selectedUser) {
                this.$toast.error('No user selected. Please try again.');
                return;
            }
            this.openChangePasswordDialog(this.selectedUser);
        },
        async changeUserPassword() {
            if (!this.changePasswordValue) {
                this.$toast.error('Please enter a new password');
                return;
            }
            if (!this.changePasswordUser || !this.changePasswordUser.id) {
                this.$toast.error('Invalid user. Please try again.');
                return;
            }
            this.changingPassword = true;
            try {
                await this.$axios.patch(`/api/users/password/${this.changePasswordUser.id}`, {
                    password: this.changePasswordValue,
                });
                this.$toast.success(`Password changed for ${this.getUserFullName(this.changePasswordUser)}`);
                this.closeChangePasswordDialog();
            } catch (error) {
                console.error('Error changing password:', error);
                this.$toast.error(error.response?.data?.message || 'Failed to change password');
            } finally {
                this.changingPassword = false;
            }
        },
        // User service methods
        async getUserTasksOverview(user_id = null) {
            try {
                // Use provided user_id or get current user
                const userId = user_id || this.$store.getters["auth/get_user"].id;
                const response = await this.$axios.get(`/api/tasks/get_user_tasks_overview/${userId}`)
                const { data } = response;

                // Store in Vuex if it's for the current user
                const currentUser = this.$store.getters["auth/get_user"];
                if (!user_id || user_id === currentUser.id) {
                    this.$store.dispatch('auth/set_user_stats', data);
                }

                return data;
            } catch (error) {
                console.log('error fetching user tasks overview - service layer', error)
                return {
                    total_tasks: 0,
                    in_progress_tasks: 0,
                    completed_tasks: 0,
                    overdue_tasks: 0,
                };
            }
        }
    },
};
</script>

<style scoped lang="scss">
.search-card {
    border-radius: 8px;
}

.filter-card {
    border-radius: 8px;

    .v-card__title {
        background-color: #f8f9fa;
        border-bottom: 1px solid #e0e0e0;
        border-radius: 8px 8px 0 0;
    }

    .filter-chips {
        .v-chip {
            margin: 2px 4px 2px 0;
            transition: all 0.2s ease;
            border-radius: 16px;

            &:hover {
                transform: translateY(-1px);
                box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
            }

            &.v-chip--active {
                transform: scale(1.05);
            }
        }

        .v-chip-group {
            display: flex;
            flex-wrap: wrap;
            gap: 4px;
        }
    }
}

.group-indicator {
    width: 4px;
    height: 32px;
    background: linear-gradient(180deg, #1976d2 0%, #42a5f5 100%);
    border-radius: 2px;
}

.user-card {
    border-radius: 12px;
    transition: all 0.3s ease;
    cursor: pointer;
    height: 100%;
    position: relative;

    &:hover {
        transform: translateY(-4px);
        box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1) !important;

        .edit-btn {
            opacity: 1;
        }
    }
}

.directory-card-limited {
    cursor: default;
}

.edit-btn {
    opacity: 0.7;
    transition: opacity 0.2s ease;

    &:hover {
        opacity: 1 !important;
        background-color: rgba(0, 0, 0, 0.05);
    }
}

.skill-chip {
    background-color: #e3f2fd !important;
    color: #1976d2 !important;
    font-weight: 500;
    font-size: 11px;
}

.more-chip {
    background-color: #f5f5f5 !important;
    color: #757575 !important;
    font-weight: 500;
    font-size: 11px;
}

.team-info {
    padding-top: 12px;
    border-top: 1px solid #e0e0e0;
}

// Dialog styling
.v-dialog {
    .v-card {
        border-radius: 12px;
    }
}
</style>

<style scoped>
/* Larger text for Delete User dialog */
.delete-user-card .v-card-title {
    font-size: 32px;
    font-weight: 700;
}
.delete-user-card .delete-user-message {
    font-size: 18px;
    color: rgba(0,0,0,0.75);
}
.delete-user-card .delete-user-name {
    font-size: 20px;
    font-weight: 700;
}
.delete-user-card .delete-user-caption {
    font-size: 14px;
}

</style>
